import { AxiosError, AxiosResponse } from "axios";

import { cartService, RawCart } from "@/services/cart";
import {
  AddOutcome,
  buildAddToCartItem,
  decideAddOutcome,
  hasOfferLine,
} from "@/services/commerceAddOutcome";
import {
  decodeTreatmentOffer,
  TreatmentOffer,
} from "@/services/commerceOffers";
import { Cart } from "@/types/cart";
import { postRequest } from "@/utils/axios";

/**
 * Tech spec 5.4 outcome of a card tap; review carries the fresh offer for a second tap.
 * refusal is the server's 400/409 message for an add the read-back cart did not confirm.
 */
export type QuoteAndAddResult =
  | { outcome: "review"; offer: TreatmentOffer }
  | { outcome: "unavailable" | "retryable" | AddOutcome; refusal?: string };

// cartId is the cart the server evaluated the offer against (null when the member had none).
type Quote<Offer> = { offer: Offer; cartId: string | null };

/**
 * The member's current quote for this ID as decode reads it, "in_cart" when the server left it
 * out because its product is in the cart, or null when it is no longer offered. Throws when the
 * refresh fails. Shared with program cards (commerceProgramOutcome).
 */
export const requoteOffer = async <Offer extends { offer_id: string }>(
  offerId: string,
  decode: (offer: unknown) => Offer | null
): Promise<Quote<Offer> | "in_cart" | null> => {
  const envelope = await postRequest(
    "/api/v1/ai/commerce-offers/",
    { offer_ids: [offerId] },
    (response: AxiosResponse) => response.data
  );
  if (envelope?.schema_version !== 1 || !Array.isArray(envelope.offers))
    throw new Error("Unreadable commerce offer envelope");
  const offers: unknown[] = envelope.offers;
  const offer = offers
    .map(decode)
    .find((decoded) => decoded?.offer_id === offerId);
  if (!offer) {
    const inCart: unknown = envelope.in_cart_offer_ids;
    return Array.isArray(inCart) && inCart.includes(offerId) ? "in_cart" : null;
  }
  const cartId: unknown = envelope.cart_id;
  if (cartId !== null && typeof cartId !== "string")
    throw new Error("Unreadable commerce offer envelope");
  return { offer, cartId };
};

// Every field the add depends on; name, description and images may change freely.
const quoteKey = (offer: TreatmentOffer): string =>
  JSON.stringify([
    offer.price,
    offer.kind,
    offer.purchase_mode,
    offer.action.type,
    buildAddToCartItem(offer),
  ]);

// Every field the offer-line predicates read; a line missing one cannot prove absence.
const isReadableLine = (line: unknown): boolean => {
  if (typeof line !== "object" || !line) return false;
  const fields = line as Record<string, unknown>;
  const product = fields.treatment_product as { id?: unknown } | null;
  return (
    (product === null || typeof product?.id === "number") &&
    typeof fields.item_type === "string" &&
    typeof fields.quantity === "number" &&
    typeof fields.is_subscription === "boolean" &&
    (fields.selling_plan_id === null ||
      typeof fields.selling_plan_id === "string") &&
    typeof fields.unavailable === "boolean"
  );
};

// normalizeCart would read a truncated response's missing line_items as an empty cart.
export const parseCompleteCart = (cart?: RawCart): Cart | undefined =>
  cart &&
  typeof cart.id === "string" &&
  Array.isArray(cart.line_items) &&
  cart.line_items.every(isReadableLine)
    ? { ...cart, line_items: cart.line_items }
    : undefined;

// A 400 refusal or 409 inactive cart explains the outcome; it never proves what the cart holds.
export const refusalMessage = (error: unknown): string | undefined => {
  const response = (error as AxiosError | undefined)?.response;
  return response &&
    [400, 409].includes(response.status) &&
    typeof response.data === "string"
    ? response.data
    : undefined;
};

/**
 * Re-quote the tapped offer and add it only when the fresh quote is unchanged.
 * A changed or withdrawn quote is refused without touching the cart. When the add
 * response cannot decide, a strict read of the same cart does. A retry is a new call,
 * so it re-quotes and re-reads the cart before any add; nothing is ever resubmitted.
 */
export async function quoteAndAdd(
  tapped: TreatmentOffer
): Promise<QuoteAndAddResult> {
  let quote: Quote<TreatmentOffer> | "in_cart" | null;
  try {
    quote = await requoteOffer(tapped.offer_id, decodeTreatmentOffer);
  } catch {
    return { outcome: "retryable" };
  }
  if (quote === "in_cart") return { outcome: "view_cart" };
  if (!quote) return { outcome: "unavailable" };
  const fresh = quote.offer;
  if (quoteKey(fresh) !== quoteKey(tapped))
    return { outcome: "review", offer: fresh };

  // Authoritative read just before the add; nothing has been submitted if it fails.
  const cartBefore = await cartService
    .getOrCreateCart(parseCompleteCart)
    .catch(() => undefined);
  if (!cartBefore) return { outcome: "retryable" };
  // The quote was evaluated against its own cart; a different one needs a fresh quote
  // (spec 5.4). A null quoted cart followed by a newly created one is fine: it is empty.
  if (quote.cartId !== null && quote.cartId !== cartBefore.id)
    return { outcome: "retryable" };
  // Adding would overwrite the existing line's mode, so never mutate it from chat.
  if (hasOfferLine(fresh, cartBefore)) return { outcome: "view_cart" };

  let cartAfter: Cart | undefined;
  let refusal: string | undefined;
  try {
    cartAfter = await cartService.addItemsToCart(
      cartBefore.id,
      [buildAddToCartItem(fresh)],
      parseCompleteCart
    );
  } catch (error) {
    refusal = refusalMessage(error);
  }
  const outcome = decideAddOutcome(fresh, cartBefore, cartAfter);
  if (outcome !== "unconfirmed") return { outcome };

  // Lost, refused or unreadable response: a failed or changed readback stays unconfirmed.
  const readback = await cartService
    .fetchCart(cartBefore.id, parseCompleteCart)
    .catch(() => undefined);
  const recovered = decideAddOutcome(fresh, cartBefore, readback);
  return recovered !== "added" && refusal
    ? { outcome: recovered, refusal }
    : { outcome: recovered };
}
